/**
 * §P.10 · Un campeonato por dentro: categorías, convocatoria y resultados.
 *
 * ## La sugerencia de categoría es lo único que solo podemos dar nosotros
 *
 * Cruza la fecha de nacimiento, el último peso anotado y el grado con las categorías
 * del campeonato. Hacerlo a mano con veinte alumnos y catorce categorías es una
 * fuente constante de errores, y aquí sale gratis de datos que ya tenemos.
 *
 * **Salen todos los alumnos, también los que no encajan en ninguna**, con el porqué.
 * El de trece años que no cabe en infantil ni en cadete porque falta una categoría es
 * justo el caso que hay que ver: una lista que lo esconde obliga a repetir el cruce a
 * mano. Por eso los que no encajan se pintan y no se filtran.
 *
 * Y `weighed_on` se enseña siempre al lado del peso: **un peso de hace tres meses no
 * sirve para inscribir**, y sin la fecha no hay forma de saberlo.
 *
 * ## Convocar es un lote con clave de idempotencia nueva por pulsación
 *
 * Emite un aviso por los tres canales a cada familia. Pulsar dos veces no puede ser
 * dos correos a veinte casas, así que la clave se genera en el momento del clic y no
 * al montar la pantalla.
 *
 * ## Retirar no borra
 *
 * `status: withdrawn`. El que se cayó a última hora es un dato, y el club que ve tres
 * retiradas del mismo alumno en una temporada sabe algo que no sabría si las filas
 * hubieran desaparecido.
 *
 * ## Las categorías se guardan enteras, **con su `id`**
 *
 * `PUT` sustituye la lista. Una categoría borrada y vuelta a crear deja sin categoría
 * a los inscritos en ella —`ON DELETE SET NULL`—, y eso es la convocatoria del sábado
 * deshecha sin que nada falle. Por eso el formulario arrastra el `id` de las que ya
 * existen en lugar de mandar una lista nueva.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, euros, fecha } from '@/shared/ui/formato';
import {
  ESTADOS_DE_INSCRIPCION,
  etiquetaDeNivel,
  MEDALLAS,
  type Campeonato,
  type CategoriaDeCampeonato,
  type CategoriaSugerida,
  type Inscripcion,
  type Medalla,
} from '@/club/competicion/tipos';

/** Una categoría en el formulario: las que existen arrastran su `id`. */
interface FilaDeCategoria {
  id: string | null;
  name: string;
  modality: string;
  age_min: string;
  age_max: string;
  weight_min_kg: string;
  weight_max_kg: string;
}

const deCategoria = (c: CategoriaDeCampeonato): FilaDeCategoria => ({
  id: c.id,
  name: c.name,
  modality: c.modality ?? '',
  age_min: c.age_min === null ? '' : String(c.age_min),
  age_max: c.age_max === null ? '' : String(c.age_max),
  weight_min_kg: c.weight_min_kg === null ? '' : String(c.weight_min_kg),
  weight_max_kg: c.weight_max_kg === null ? '' : String(c.weight_max_kg),
});

const VACIA: FilaDeCategoria = {
  id: null,
  name: '',
  modality: '',
  age_min: '',
  age_max: '',
  weight_min_kg: '',
  weight_max_kg: '',
};

const numeroONulo = (v: string): number | null => {
  const n = Number(v.replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
};

export function FichaDeCampeonato({
  club,
  campeonatoId,
  puedeEditar,
  alCerrar,
}: {
  club: string;
  campeonatoId: string;
  puedeEditar: boolean;
  alCerrar: () => void;
}) {
  const cache = useQueryClient();
  const [problema, setProblema] = useState<Problema | null>(null);
  const [editandoCategorias, setEditandoCategorias] = useState(false);
  const [filas, setFilas] = useState<FilaDeCategoria[]>([]);
  const [elegidos, setElegidos] = useState<Record<string, string>>({});
  const [resultados, setResultados] = useState<
    Record<string, { placement: string; medal: string }>
  >({});

  const campeonato = useQuery({
    queryKey: ['campeonato', club, campeonatoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        .get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Campeonato;
    },
  });

  const categorias = useQuery({
    queryKey: ['categorias', club, campeonatoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        .categories.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as CategoriaDeCampeonato[];
    },
  });

  const inscripciones = useQuery({
    queryKey: ['inscripciones', club, campeonatoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        .registrations.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Inscripcion[];
    },
  });

  const sugeridas = useQuery({
    queryKey: ['sugeridas', club, campeonatoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        ['suggested-categories'].get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as CategoriaSugerida[];
    },
  });

  const refrescar = () => {
    setProblema(null);
    for (const clave of [
      'campeonato',
      'categorias',
      'inscripciones',
      'sugeridas',
      'campeonatos',
    ]) {
      void cache.invalidateQueries({ queryKey: [clave, club] });
    }
  };

  const guardarCategorias = useMutation({
    mutationFn: async (lista: FilaDeCategoria[]) => {
      const r = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        .categories.put({
          categories: lista
            .filter((f) => f.name.trim() !== '')
            .map((f) => ({
              // El `id` viaja en las que ya existen: ver la cabecera.
              ...(f.id === null ? {} : { id: f.id }),
              name: f.name.trim(),
              modality: f.modality.trim() === '' ? null : f.modality.trim(),
              age_min: numeroONulo(f.age_min),
              age_max: numeroONulo(f.age_max),
              weight_min_kg: numeroONulo(f.weight_min_kg),
              weight_max_kg: numeroONulo(f.weight_max_kg),
            })),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setEditandoCategorias(false);
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const convocar = useMutation({
    mutationFn: async (
      candidatos: { student_id: string; category_id?: string | null }[]
    ) => {
      const r = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        .registrations.post(
          { candidates: candidatos },
          // Clave nueva **por pulsación**: manda un aviso por familia.
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setElegidos({});
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiarInscripcion = useMutation({
    mutationFn: async ({
      id,
      cambios,
    }: {
      id: string;
      cambios: Record<string, unknown>;
    }) => {
      const r = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        .registrations({ registrationId: id })
        .patch(cambios);
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  const guardarResultados = useMutation({
    mutationFn: async () => {
      const lista = Object.entries(resultados)
        .filter(([, r]) => r.placement !== '' || r.medal !== '')
        .map(([registration_id, r]) => ({
          registration_id,
          placement: r.placement === '' ? null : Number(r.placement),
          medal: r.medal === '' ? null : (r.medal as Medalla),
        }));

      if (lista.length === 0) return null;

      const r = await api.v1
        .clubs({ club })
        .competitions({ id: campeonatoId })
        .results.put({ results: lista });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setResultados({});
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (campeonato.isPending) {
    return <Cargando filas={5} etiqueta="Cargando el campeonato" />;
  }

  if (campeonato.isError || !campeonato.data) {
    return (
      <PanelError
        problema={aProblema(campeonato.error)}
        reintentar={() => void campeonato.refetch()}
      />
    );
  }

  const c = campeonato.data;
  const editable = puedeEditar && c.is_mine;
  const yaConvocados = new Set(
    (inscripciones.data ?? []).map((i) => i.student_id)
  );
  const porConvocar = Object.keys(elegidos).length;

  return (
    <section>
      <p className="volver">
        <button type="button" className="enlace-fila" onClick={alCerrar}>
          ← Todos los campeonatos
        </button>
      </p>

      <header className="titulo-fila">
        <h1>{c.name}</h1>
        <span className="pastilla pastilla-left">
          {etiquetaDeNivel(c.level)}
        </span>
      </header>

      <p className="tenue">
        {fecha(c.starts_on)}
        {c.city && ` · ${c.city}`}
        {c.venue_name && ` · ${c.venue_name}`} · {c.discipline_name}
        {c.federation && ` · ${c.federation}`}
        {c.registration_deadline &&
          ` · se inscribe hasta el ${fecha(c.registration_deadline)}`}
      </p>

      {!c.is_mine && (
        <p className="tenue">
          Lo organiza otro club o la federación: aquí decides a quién llevas, no
          cómo es el campeonato.
        </p>
      )}

      {problema && <PanelError problema={problema} />}

      {/* ── Categorías ──────────────────────────────────────────────────── */}
      <h2 className="seccion">Categorías</h2>

      {categorias.isPending ? (
        <Cargando filas={2} etiqueta="Cargando categorías" />
      ) : editandoCategorias ? (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            guardarCategorias.mutate(filas);
          }}
        >
          {filas.map((f, i) => (
            <div className="rejilla" key={f.id ?? `nueva-${i}`}>
              <label>
                Categoría
                <input
                  required
                  maxLength={120}
                  placeholder="Infantil masculino -45 kg"
                  value={f.name}
                  onChange={(e) =>
                    setFilas(
                      filas.map((x, j) =>
                        i === j ? { ...x, name: e.target.value } : x
                      )
                    )
                  }
                />
              </label>

              <label>
                Modalidad
                <input
                  maxLength={40}
                  placeholder="Combate"
                  value={f.modality}
                  onChange={(e) =>
                    setFilas(
                      filas.map((x, j) =>
                        i === j ? { ...x, modality: e.target.value } : x
                      )
                    )
                  }
                />
              </label>

              <label>
                Edad desde
                <input
                  type="number"
                  min={0}
                  value={f.age_min}
                  onChange={(e) =>
                    setFilas(
                      filas.map((x, j) =>
                        i === j ? { ...x, age_min: e.target.value } : x
                      )
                    )
                  }
                />
              </label>

              <label>
                Edad hasta
                <input
                  type="number"
                  min={0}
                  value={f.age_max}
                  onChange={(e) =>
                    setFilas(
                      filas.map((x, j) =>
                        i === j ? { ...x, age_max: e.target.value } : x
                      )
                    )
                  }
                />
              </label>

              <label>
                Peso desde (kg)
                <input
                  inputMode="decimal"
                  value={f.weight_min_kg}
                  onChange={(e) =>
                    setFilas(
                      filas.map((x, j) =>
                        i === j ? { ...x, weight_min_kg: e.target.value } : x
                      )
                    )
                  }
                />
              </label>

              <label>
                Peso hasta (kg)
                <input
                  inputMode="decimal"
                  value={f.weight_max_kg}
                  onChange={(e) =>
                    setFilas(
                      filas.map((x, j) =>
                        i === j ? { ...x, weight_max_kg: e.target.value } : x
                      )
                    )
                  }
                />
              </label>
            </div>
          ))}

          <div className="acciones">
            <button
              type="button"
              className="btn-plano"
              onClick={() => setFilas([...filas, { ...VACIA }])}
            >
              Añadir otra
            </button>
            <button
              type="submit"
              className="btn-primario"
              disabled={guardarCategorias.isPending}
            >
              {guardarCategorias.isPending ? 'Guardando…' : 'Guardar'}
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setEditandoCategorias(false)}
            >
              Dejarlo
            </button>
          </div>

          {/*
            Se dice al lado del botón: quitar una fila aquí deja sin categoría a
            quien estuviera inscrito en ella, y eso no lo avisa ningún error.
          */}
          <p className="nota">
            <strong>
              Quitar una categoría deshace las inscripciones que tuviera.
            </strong>{' '}
            Los alumnos se quedan convocados pero sin categoría, y hay que
            volver a asignársela.
          </p>
        </form>
      ) : (categorias.data ?? []).length === 0 ? (
        <>
          <p className="tenue">
            Todavía no hay categorías. Sin ellas se puede convocar, pero no
            calcular a quién le toca cuál.
          </p>
          {editable && (
            <div className="acciones">
              <button
                type="button"
                className="btn-primario"
                onClick={() => {
                  setFilas([{ ...VACIA }]);
                  setEditandoCategorias(true);
                }}
              >
                Definir las categorías
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <ul className="lista-clara">
            {(categorias.data ?? []).map((cat) => (
              <li key={cat.id}>
                <strong>{cat.name}</strong>
                <span className="tenue">
                  {cat.modality && ` · ${cat.modality}`}
                  {(cat.age_min !== null || cat.age_max !== null) &&
                    ` · ${cat.age_min ?? '?'}–${cat.age_max ?? '?'} años`}
                  {(cat.weight_min_kg !== null || cat.weight_max_kg !== null) &&
                    ` · ${cat.weight_min_kg ?? '?'}–${cat.weight_max_kg ?? '?'} kg`}
                </span>
              </li>
            ))}
          </ul>

          {editable && (
            <div className="acciones">
              <button
                type="button"
                className="btn-plano"
                onClick={() => {
                  setFilas((categorias.data ?? []).map(deCategoria));
                  setEditandoCategorias(true);
                }}
              >
                Editar las categorías
              </button>
            </div>
          )}
        </>
      )}

      {/* ── A quién llevar ──────────────────────────────────────────────── */}
      <h2 className="seccion">A quién llevar</h2>

      <p className="tenue">
        La categoría sale del cruce entre la edad, el último peso anotado y el
        grado.{' '}
        <strong>Salen todos, también los que no encajan en ninguna</strong>: ese
        es justo el caso que hay que ver.
      </p>

      {sugeridas.isPending ? (
        <Cargando filas={3} etiqueta="Calculando categorías" />
      ) : (sugeridas.data ?? []).length === 0 ? (
        <p className="tenue">No hay alumnos activos que puedan competir.</p>
      ) : (
        <>
          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Alumno</th>
                  <th className="num">Edad</th>
                  <th>Peso</th>
                  <th>Le tocaría</th>
                  <th>Llevar</th>
                </tr>
              </thead>
              <tbody>
                {(sugeridas.data ?? []).map((s) => {
                  const convocado = yaConvocados.has(s.student_id);
                  return (
                    <tr key={s.student_id}>
                      <td>
                        {s.first_name} {s.last_name ?? ''}
                      </td>
                      <td className="num">{s.age ?? '—'}</td>
                      <td>
                        {s.weight_kg === null ? (
                          <span className="tenue">sin pesar</span>
                        ) : (
                          <>
                            {s.weight_kg} kg
                            {/*
                              La fecha al lado y siempre: un peso de hace tres
                              meses no sirve para inscribir, y sin ella no hay
                              forma de saberlo.
                            */}
                            <span className="tenue">
                              {' '}
                              · {fecha(s.weighed_on)}
                            </span>
                          </>
                        )}
                      </td>
                      <td>
                        {s.category_name ?? (
                          <span className="tenue">{s.reason}</span>
                        )}
                        {s.alternatives > 0 && (
                          <span className="tenue">
                            {' '}
                            · y{' '}
                            {cuenta(
                              s.alternatives,
                              'opción más',
                              'opciones más'
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        {convocado ? (
                          <span className="tenue">Ya convocado</span>
                        ) : (
                          editable && (
                            <input
                              type="checkbox"
                              aria-label={`Convocar a ${s.first_name}`}
                              checked={s.student_id in elegidos}
                              onChange={(e) => {
                                const copia = { ...elegidos };
                                if (e.target.checked)
                                  copia[s.student_id] = s.category_id ?? '';
                                else delete copia[s.student_id];
                                setElegidos(copia);
                              }}
                            />
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {editable && porConvocar > 0 && (
            <div className="acciones">
              <button
                type="button"
                className="btn-primario"
                disabled={convocar.isPending}
                onClick={() =>
                  convocar.mutate(
                    Object.entries(elegidos).map(([student_id, cat]) => ({
                      student_id,
                      ...(cat === '' ? {} : { category_id: cat }),
                    }))
                  )
                }
              >
                {convocar.isPending
                  ? 'Convocando…'
                  : `Convocar a ${cuenta(porConvocar, 'alumno', 'alumnos')}`}
              </button>
              <span className="tenue">
                A cada familia le llega un aviso: tiene que reservar el sábado.
              </span>
            </div>
          )}
        </>
      )}

      {/* ── La convocatoria ─────────────────────────────────────────────── */}
      <h2 className="seccion">Convocatoria</h2>

      {inscripciones.isPending ? (
        <Cargando filas={3} etiqueta="Cargando la convocatoria" />
      ) : (inscripciones.data ?? []).length === 0 ? (
        <Vacio
          titulo="Todavía no has convocado a nadie"
          explicacion="Marca arriba a quién llevas y pulsa «Convocar». A cada familia le llegará un aviso para que reserve el día."
        />
      ) : (
        <ul className="papeles">
          {(inscripciones.data ?? []).map((i) => (
            <li
              key={i.id}
              className={i.status === 'withdrawn' ? 'fila-fuera' : undefined}
            >
              <div className="aviso-cabeza">
                <strong>
                  {i.first_name} {i.last_name ?? ''}
                </strong>
                <span className="pastilla pastilla-left">
                  {ESTADOS_DE_INSCRIPCION[i.status]}
                </span>
                {i.medal && (
                  <span className="pastilla pastilla-bien">{i.medal}</span>
                )}
              </div>

              <p className="tenue">
                {i.category_name ?? 'Sin categoría asignada'}
                {i.weigh_in_kg !== null && ` · pesó ${i.weigh_in_kg} kg`}
                {i.fee_cents !== null && ` · ${euros(i.fee_cents)}`}
                {i.placement !== null && ` · ${i.placement}.º puesto`}
              </p>

              {editable && i.status !== 'withdrawn' && (
                <div className="acciones">
                  <label className="fecha-en-linea">
                    Categoría
                    <select
                      value={i.category_id ?? ''}
                      disabled={cambiarInscripcion.isPending}
                      onChange={(e) =>
                        cambiarInscripcion.mutate({
                          id: i.id,
                          cambios: {
                            category_id:
                              e.target.value === '' ? null : e.target.value,
                          },
                        })
                      }
                    >
                      <option value="">Sin asignar</option>
                      {(categorias.data ?? []).map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/*
                    Retirar es un estado, no un borrado: tres retiradas del mismo
                    alumno en una temporada dicen algo que las filas borradas no
                    dirían.
                  */}
                  <button
                    type="button"
                    className="btn-plano"
                    disabled={cambiarInscripcion.isPending}
                    onClick={() =>
                      cambiarInscripcion.mutate({
                        id: i.id,
                        cambios: { status: 'withdrawn' },
                      })
                    }
                  >
                    Retirar
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* ── Resultados ──────────────────────────────────────────────────── */}
      {(inscripciones.data ?? []).some((i) => i.status !== 'withdrawn') && (
        <>
          <h2 className="seccion">Resultados</h2>

          <p className="tenue">
            Se copian de la hoja de la federación y siempre hay uno mal, así que
            corregir no crea otra fila.{' '}
            <strong>
              El aviso a la familia sale solo por quien tiene medalla
            </strong>
            : un push para decirle a una madre que su hija quedó cuarta es
            recordarle que no subió al podio.
          </p>

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Alumno</th>
                  <th>Categoría</th>
                  <th className="num">Puesto</th>
                  <th>Medalla</th>
                </tr>
              </thead>
              <tbody>
                {(inscripciones.data ?? [])
                  .filter((i) => i.status !== 'withdrawn')
                  .map((i) => {
                    const r = resultados[i.id] ?? {
                      placement:
                        i.placement === null ? '' : String(i.placement),
                      medal: i.medal ?? '',
                    };
                    return (
                      <tr key={i.id}>
                        <td>
                          {i.first_name} {i.last_name ?? ''}
                        </td>
                        <td className="tenue">{i.category_name ?? '—'}</td>
                        <td className="num">
                          <input
                            type="number"
                            min={1}
                            disabled={!editable}
                            value={r.placement}
                            onChange={(e) =>
                              setResultados({
                                ...resultados,
                                [i.id]: { ...r, placement: e.target.value },
                              })
                            }
                          />
                        </td>
                        <td>
                          <select
                            disabled={!editable}
                            value={r.medal}
                            onChange={(e) =>
                              setResultados({
                                ...resultados,
                                [i.id]: { ...r, medal: e.target.value },
                              })
                            }
                          >
                            <option value="">Sin medalla</option>
                            {MEDALLAS.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.etiqueta}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {editable && Object.keys(resultados).length > 0 && (
            <div className="acciones">
              <button
                type="button"
                className="btn-primario"
                disabled={guardarResultados.isPending}
                onClick={() => {
                  setProblema(null);
                  guardarResultados.mutate();
                }}
              >
                {guardarResultados.isPending
                  ? 'Guardando…'
                  : 'Guardar los resultados'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
